/**
 * Shared types and utilities for order/return slices
 */
import { AxiosError } from 'axios';
import { ReturnItem, ReturnOrderLine, ReturnSlip, CustomerCard } from 'returnCenter-api';
import { toast } from 'react-toastify';
import { t } from 'i18next';

// Extended types
export type ReturnItemProperty =
    | 'quantityInReturn'
    | 'percent'
    | 'returnToStock'
    | 'comment'
    | 'externalComment'
    | 'reasonCode'
    | 'decisionCode'
    | 'isManualOverride'
    | 'decisionCodeIsManual'
    | 'status'
    | 'lpn'
    | 'batchReturnId'
    | 'intercompanyDecisionCode'
    | 'intercompanyDecisionCodeIsManual'
    | 'intercompanyReasonCode'
    | 'intercompanyReasonCodeIsManual';

export interface ExtendedReturnItem extends ReturnItem {
    splitId?: string;
    isSplitRow?: boolean;
}

export interface ExtendedReturnOrderLine extends ReturnOrderLine {
    skuLocation?: string | null;
}

export interface ExtendedReturnOrderLine extends ReturnOrderLine {
    skuLocation?: string | null;
}

export type ExtendedAddress = {
    customerCode?: string | null | number;
    customerCard?: CustomerCard;
};

export type LoadingCards = Record<
    | 'completed'
    | 'onHold'
    | 'inprogress'
    | 'created'
    | 'itemsSelected'
    | 'incomingPackage'
    | 'received'
    | 'creditError'
    | 'processing'
    | 'creditedBatchPending',
    boolean
>;

export type SearchReturnPageInfo = {
    pageSize?: number;
    currentPage?: number;
    totalItems?: number;
    totalPages?: number;
    hasNextPage?: boolean;
    hasPreviousPage?: boolean;
};

export type LocalPropertySettings = {
    reasonCode?: number;
    percent?: number;
    returnToStock?: boolean;
};

// Utility functions
export function canAddSplit(
    orderLine: ReturnOrderLine,
    _itemId: string | number,
    splitId: string | number,
    newSplitQty: number = 1,
): boolean {
    const totalQtyInReturnItems = orderLine.returnItems?.reduce((sum, item) => {
        if ((item as ExtendedReturnItem).splitId === splitId || item.id === splitId) {
            return sum + newSplitQty;
        }
        return sum + (item.quantityInReturn || 0);
    }, 0);

    if (orderLine.quantityAvailableForReturn && totalQtyInReturnItems !== undefined) {
        return totalQtyInReturnItems < orderLine.quantityAvailableForReturn;
    }
    return false;
}

export function findOrderLine(
    returnSlip: ReturnSlip | null,
    orderNumber: string,
    orderLineNumber: string,
    sourceReturnId?: number | null,
): ReturnOrderLine | undefined {
    const orderGroup = returnSlip?.returnItems?.find(
        (group) =>
            group.orderNumber === orderNumber &&
            (sourceReturnId === undefined || group.sourceReturnId === sourceReturnId),
    );
    return orderGroup?.orderLines?.find((line) => line.orderLineNumber === orderLineNumber);
}

export function findSplitItem(
    returnSlip: ReturnSlip | null,
    orderNumber: string,
    orderLineNumber: string,
    splitId: string | number,
    sourceReturnId?: number | null,
): ExtendedReturnItem | undefined {
    const orderLine = findOrderLine(returnSlip, orderNumber, orderLineNumber, sourceReturnId);
    return orderLine?.returnItems?.find((item: ExtendedReturnItem) =>
        item.splitId ? item.splitId === splitId : item.id === splitId,
    );
}

export function handleApiError({ error, rejectWithValue }: { error: AxiosError; rejectWithValue: Function }) {
    const status = error?.response?.status;
    if (status === 404) {
        toast.error(t('error.returnSlipNotFound'), { autoClose: 3500, closeOnClick: true });
        return rejectWithValue({ message: t('error.returnSlipNotFound'), status: 404 });
    }
    if (status === 500) {
        toast.error(t('error.internalServerError'), { autoClose: 3500, closeOnClick: true });
        return rejectWithValue({ message: t('error.internalServerError'), status: 500 });
    }
    toast.error(t('error.unknownError'), { autoClose: 3500, closeOnClick: true });
    return rejectWithValue({ message: t('error.unknownError') });
}
